import { Helmet } from 'react-helmet-async';
import { AgGridReact } from 'ag-grid-react';
import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom'
import { enqueueSnackbar } from 'notistack';
import { Box, Button, CircularProgress, Stack, Typography, IconButton, Tooltip } from '@mui/material';
import axiosInstance from '../../utils/axios';
import EditIconButton from '../../reuseable-components/DefaultButtons/EditIconButton';
import DeleteIconButton from '../../reuseable-components/DefaultButtons/DeleteIconButton';
import FacilityForm from './FacilityForm';
import Iconify from '../../reuseable-components/iconify/Iconify';
import AgGridDefaults from '../../reuseable-components/AGGrid/AGGridDefaults';
import AGGridQuickFilterWrapper from '../../reuseable-components/AGGrid/AGGridQuickFilterWrapper';

export default function FacilityList() {
  const gridRef = useRef(null);
  const navigate = useNavigate();
  const [facilities, setFacilities] = useState([]);
  const [parents, setParents] = useState([]);
  const [purchasingGroups, setPurchasingGroups] = useState([]);
  const [contactTypes, setContactTypes] = useState([]);
  const [editFacility, setEditFacility] = useState(null);
  const [openForm, setOpenForm] = useState(false);
  const [loading, setLoading] = useState(true);



  useEffect(() => {
    setLoading(true);
    axiosInstance.get("/facility/facilities")
      .then(res => {
        setFacilities(res.data.facilities.map(facility => ({
            ...facility,
          parentName: res.data.parents.find(parent => parent.value === facility.parentId)?.label,
          purchasingGroupName: res.data.purchasingGroups.find(purchasingGroup => purchasingGroup.value === facility.purchasingGroupId)?.label
        })));
        setParents(res.data.parents);
        setPurchasingGroups(res.data.purchasingGroups);
        setContactTypes(res.data.contactTypes);
      })
      .catch(() => enqueueSnackbar('Facility list failed', { variant: 'error' }))
      .finally(() => setLoading(false));
  }, []);

  const deleteFacility = (id) => {
    axiosInstance.post(`/facility/deleteFacilty?id=${id}`)
      .then(() => {
        setFacilities(facilities => [...facilities.filter(facility => facility.id !== id)]);
        enqueueSnackbar('Facility deleted', { variant: 'success' })
      })
      .catch(() => enqueueSnackbar('Facility delete failed', { variant: 'error' }))
    };  


  const columns = useMemo(() => [
    {
      field: "name",
      minWidth: 150,
      flex: 1,
    },
    {
      field: "purchasingGroupName",
      headerName: "Purchasing Group",
      minWidth: 100,
    },
    {
      field: "parentName",
      headerName: "Parent",
      minWidth: 100,
    },
    {
      field: "shippingAddress.streetAddress",
      headerName: "Address",
      minWidth: 100,
    },
    {
      field: "shippingAddress.city",
      headerName: "City",
      minWidth: 150,
    },
    {
      field: "shippingAddress.state",
      headerName: "State",
      minWidth: 150,
    },
    {
      field: "facilityCode",
      minWidth: 100,
    },
    {
      field: "actions",
      minWidth: 100,
      cellRenderer: (params) => (
        <Stack direction="row" spacing={1}>
          <Tooltip title='Facility Info' arrow placement="top">
            <IconButton edge="end" color="secondary" disableRipple sx={{ px: 0 }} onClick={() => navigate(`/admin/FacilityInfo/${params.data.id}`)}>
              <Iconify icon="eva:info-outline" />
            </IconButton>
          </Tooltip>
          <EditIconButton
            onClick={() => {
              setEditFacility(params.data);
              setOpenForm(true);
            }}
          />     
          <DeleteIconButton
            content={`Are you sure you want to delete ${params.data.name}?`}
            action={() => deleteFacility(params.data.id)}
          />
        </Stack>
      ),
    },
  ], []);

  return (<>
    <Helmet>
      <title>Facility List | NHIDirect</title>
    </Helmet>

    <Box sx={{ height: "calc(100% - 55px)", mt: 2 }}>
      <AGGridQuickFilterWrapper gridRef={gridRef}>
        <Button variant="contained" startIcon={<Iconify icon="eva:plus-fill" />} onClick={() => { setEditFacility(null); setOpenForm(true); }} color="primary" size="small">
          <Typography>Add Facility</Typography>
        </Button>
      </AGGridQuickFilterWrapper>
      {!loading ? <Box sx={{ height: "max(500px, 100%)" }}>
        <AgGridReact ref={gridRef} {...AgGridDefaults} columnDefs={columns} rowData={facilities} />
      </Box> : <Box sx={{ display: "flex", justifyContent: "center", m: 3 }}><CircularProgress /></Box>}
      <FacilityForm editFacility={editFacility} open={openForm} close={() => { setEditFacility(null); setOpenForm(false); }}
      parents={ parents} purchasingGroups={purchasingGroups} setFacilities={setFacilities} contactTypes={contactTypes} />
    </Box>
  </>);
}
